import { uniqBy } from 'lodash';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { User } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { useResourceManager } from 'components/organisms/resource-management/managers';
import {
  ProjectsWithMembers,
  SchedulerReference,
} from 'components/organisms/resource-management/types';
import { AllocatedMembers } from 'data/templates/projects';
import { updateAllocatedMembersAction } from 'data/templates/projects/actions';

interface Props {
  schedulerRef: SchedulerReference;
  allocatedMembers: AllocatedMembers;
  project: ProjectsWithMembers;
  addResource: ReturnType<typeof useResourceManager>['addResource'];
}
interface Result {
  addUsersModalProjectId?: number;
  showAddUsersModal: () => void;
  hideAddUsersModal: () => void;
  onUsersAdded: (projectId: number, userList: User[]) => void;
}
export const useAddUsersManager = ({
  allocatedMembers,
  project,
  schedulerRef,
  addResource,
}: Props): Result => {
  const dispatch = useDispatch();
  const [addUserToProjectId, setAddUserToProjectId] = useState<number>();
  const showAddUsersModal = useCallback(() => {
    setAddUserToProjectId(project.projectId);
  }, [project.projectId]);
  const hideAddUsersModal = useCallback(() => {
    setAddUserToProjectId(undefined);
  }, []);
  const addUsersToState = useCallback(
    (users: User[]) => {
      dispatch(
        updateAllocatedMembersAction({
          teams: allocatedMembers.teams,
          users: uniqBy(allocatedMembers.users.concat(users), 'userId'),
        }),
      );
    },
    [allocatedMembers.teams, allocatedMembers.users, dispatch],
  );
  const onUsersAdded = useCallback(
    (projectId: number, userList: User[]) => {
      addUsersToState(userList);
      schedulerRef.current.instance.suspendRefresh();
      userList.forEach((user) =>
        addResource({
          projectId,
          resource: {
            type: ResourceType.User,
            user,
          },
        }),
      );
      schedulerRef.current.instance.resumeRefresh(true);
      setAddUserToProjectId(undefined);
    },
    [addResource, addUsersToState, schedulerRef],
  );

  return {
    addUsersModalProjectId: addUserToProjectId,
    showAddUsersModal,
    hideAddUsersModal,
    onUsersAdded,
  };
};
